import { ArrowLeft, Loader2 } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AdminLayout } from "@sd/components/admin/AdminLayout";
import { TicketDetailIssueView } from "@sd/components/tickets/TicketDetailIssueView";
import { useAuth } from "@sd/hooks/useAuth";
import { useTicketDetailPage } from "@sd/hooks/useTicketDetailPage";
import { getDefaultSdPath, sd, type SdAppRole } from "@sd/lib/sdPaths";
import { isAgentShellRole } from "@sd/lib/sdRoles";
import { TicketPanelShell } from "@sd/components/layout/TicketPanelShell";
import { parseTicketDetailReturnTo, inferTicketQueueFromReturnTo } from "@sd/lib/ticketDetailPath";

function defaultBackPath(role: SdAppRole | null): string {
  if (role === "ServiceDesk_Admin") return sd("admin/tickets");
  if (role === "ServiceDesk_SuperAgent") return sd("tickets/project-status");
  if (isAgentShellRole(role)) return sd("tickets/my-tickets");
  if (role === "ServiceDesk_User") return sd("my-portal");
  return getDefaultSdPath(role);
}

function TicketDetailContent({
  ticketId,
  onBack,
  showAddNoteSection,
}: {
  ticketId: string;
  onBack: () => void;
  showAddNoteSection: boolean;
}) {
  const {
    ticket,
    display,
    attachments,
    attachmentsLoading,
    activityEntries,
    activityPending,
    activityError,
    notes,
    notesPending,
    notesError,
    resolveUserLabel,
    isLoading,
    isError,
  } = useTicketDetailPage(ticketId);

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError || !ticket) {
    return (
      <div className="p-4 sm:p-6">
        <Button variant="ghost" size="sm" className="mb-4 gap-2" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <p className="text-sm text-destructive">
          Could not load ticket details. The ticket may not exist or you may not
          have access.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="min-w-0 w-full border-b bg-background/95 p-4 backdrop-blur sm:p-6 sm:pb-3">
        <Button variant="ghost" size="sm" className="gap-2" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
      </div>
      <TicketDetailIssueView
        ticket={ticket}
        display={display}
        activityEntries={activityEntries}
        activityPending={activityPending}
        activityError={activityError}
        notes={notes}
        notesPending={notesPending}
        notesError={notesError}
        attachments={attachments}
        attachmentsLoading={attachmentsLoading}
        resolveUserLabel={resolveUserLabel}
        showAddNoteSection={showAddNoteSection}
      />
    </>
  );
}

export default function TicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { role, isLoading: authLoading } = useAuth();

  const returnTo =
    parseTicketDetailReturnTo(searchParams) ?? defaultBackPath(role);

  const handleBack = () => {
    navigate(returnTo);
  };

  const id = ticketId?.trim() ?? "";

  if (authLoading && !role) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!id) {
    return (
      <div className="p-4 sm:p-6">
        <p className="text-sm text-muted-foreground">Invalid ticket link.</p>
        <Button variant="link" className="mt-2 px-0" onClick={handleBack}>
          Go back
        </Button>
      </div>
    );
  }

  const content = (
    <TicketDetailContent
      ticketId={id}
      onBack={handleBack}
      showAddNoteSection={role === "ServiceDesk_Admin"}
    />
  );

  if (role === "ServiceDesk_Admin") {
    return <AdminLayout>{content}</AdminLayout>;
  }

  if (role === "ServiceDesk_SuperAgent" || isAgentShellRole(role)) {
    const activeQueue = inferTicketQueueFromReturnTo(returnTo, role);
    return (
      <TicketPanelShell activeQueue={activeQueue}>{content}</TicketPanelShell>
    );
  }

  return <div className="min-h-screen bg-background">{content}</div>;
}
